import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, inject } from "@angular/core";
import type { Guid, IResponse } from "@kiraah-shared/common";
import { LvfOperationType, LvfRequestSource } from "@kiraah-shared/common";
import { ENVIRONMENT } from "@kiraah-shared/environment";
import { Observable, mergeMap, of, throwError } from "rxjs";
import { MOBILE_GATEWAY_URL } from "../../auth/mobile-auth.config";
import { MOBILE_EACHAT_TICKET_PROFILE_MODULE } from "../helpers/mobile-eachat-app-access";
import { MobileAuthStore } from "../../state/mobile-auth.store";
import { MobileProfileStore } from "../../state/mobile-profile.store";
import type {
  IMobileVoiceTicketDemand,
  IMobileVoiceTicketGenerateResult,
  IMobileVoiceTicketSubmitTranscriptRequest,
} from "../models/mobile-voice-ticket.models";

@Injectable({ providedIn: "root" })
export class MobileVoiceTicketApiClient {
  private readonly http = inject(HttpClient);
  private readonly authStore = inject(MobileAuthStore);
  private readonly profileStore = inject(MobileProfileStore);
  private readonly environment = inject(ENVIRONMENT);

  private readonly baseUrl: string;

  constructor(@Inject(MOBILE_GATEWAY_URL) gatewayUrl: string) {
    this.baseUrl = `${gatewayUrl}/api/core/voice-tickets`;
  }

  submitTranscript(request: IMobileVoiceTicketSubmitTranscriptRequest): Observable<IMobileVoiceTicketDemand> {
    return this.http
      .post<IResponse<IMobileVoiceTicketDemand>>(`${this.baseUrl}/transcripts`, request, {
        headers: this.lvfHeaders(LvfOperationType.CREATE),
      })
      .pipe(mergeMap((response) => this.unwrap(response, "mobile.eachat.voice.error.submitFailed")));
  }

  getActiveDraft(): Observable<IMobileVoiceTicketDemand> {
    return this.http
      .get<IResponse<IMobileVoiceTicketDemand>>(`${this.baseUrl}/authors/active-draft`, {
        headers: this.lvfHeaders(LvfOperationType.READ),
      })
      .pipe(mergeMap((response) => this.unwrap(response, "mobile.eachat.voice.error.activeDraftFailed")));
  }

  generateDraft(demandUid: Guid): Observable<IMobileVoiceTicketGenerateResult> {
    return this.http
      .post<IResponse<IMobileVoiceTicketGenerateResult>>(
        `${this.baseUrl}/${demandUid}/generate-draft`,
        null,
        { headers: this.lvfHeaders(LvfOperationType.UPDATE) },
      )
      .pipe(mergeMap((response) => this.unwrap(response, "mobile.eachat.voice.error.generateFailed")));
  }

  cancelDemand(demandUid: Guid): Observable<boolean> {
    return this.http
      .delete<IResponse<boolean>>(`${this.baseUrl}/${demandUid}`, {
        headers: this.lvfHeaders(LvfOperationType.DELETE),
      })
      .pipe(mergeMap((response) => this.unwrap(response, "mobile.eachat.voice.error.cancelFailed")));
  }

  private unwrap<T>(response: IResponse<T>, fallbackKey: string): Observable<T> {
    if (!response?.IsSuccessful || response.Data == null) {
      return throwError(() => new Error(response?.MessageKey ?? response?.Message ?? fallbackKey));
    }
    return of(response.Data);
  }

  private lvfHeaders(operation: LvfOperationType): HttpHeaders {
    const identityUid = this.authStore.authResult()?.ProfilData?.Identity?.Uid ?? "";
    const appUid = this.profileStore.activeApplication()?.Uid ?? "APP-EMPTY-UID";
    return new HttpHeaders({
      "Content-Type": "application/json",
      "X-CONNECTED-IDENTITY-UID": `${identityUid}`,
      "X-USER-TOKEN-UID": "",
      "X-APP-UID": `${appUid}`,
      "X-APP-MODULE-UID": `${MOBILE_EACHAT_TICKET_PROFILE_MODULE}`,
      "X-REQUEST-SRC": `${LvfRequestSource.MOBILE}`,
      "X-OPERATION-TYPE": `${operation}`,
      "X-WEB-APP-URL": `${this.environment.WebAppUrl ?? ""}`,
    });
  }
}
